import React, { useEffect } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import {
  fetchReportsConfig,
  selectReportByUniqueCode,
  selectReportsConfigHasAttemptedFetch,
  selectReportsConfigIsLoading,
} from 'src/store/slices/reportsConfigSlice';
import { usePermissions } from 'src/hooks/usePermissions';
import { canUserAccessReport } from 'src/utils/reportVisibilityUtils';
import { isValidReportUniqueCode } from 'src/types/admin/reportsConfig.types';
import { ReportRouteParams } from 'src/types/routes/routes.types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { NoAccessToVerity } from '../common/NoAccessToVerity';
import { ReportsLayout } from './ReportsLayout';

/**
 * ReportRouteGuard component
 *
 * Guards report routes based on the report's visibility level and user permissions.
 * Shows contextual error pages instead of silently redirecting:
 * - Report not found / invalid code → "This report does not exist" with Go to Home button
 * - No access → "You do not have access to this report" with Request Access button (lock image)
 *
 * Access is determined by `canUserAccessReport` which checks:
 * - TECH_ADMIN visibility: Only Tech Admins can access
 * - VERITY_ADMIN visibility: Tech Admins OR Verity Admins can access
 * - READ_ONLY visibility: All authenticated users can access
 *
 * @example
 * <Route path="/reports/:report_unique_code" element={<ReportRouteGuard />}>
 *   <Route index element={<ReportViewPage />} />
 * </Route>
 */
const ReportRouteGuard: React.FC = () => {
  const { report_unique_code } = useParams<ReportRouteParams>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userPermission } = usePermissions();

  // Get report config state from Redux
  const isValidCode = report_unique_code ? isValidReportUniqueCode(report_unique_code) : false;
  const currentReport = useAppSelector(selectReportByUniqueCode(isValidCode ? (report_unique_code ?? '') : '')) ?? null;
  const hasAttemptedFetch = useAppSelector(selectReportsConfigHasAttemptedFetch);
  const isLoadingConfig = useAppSelector(selectReportsConfigIsLoading);

  // Ensure reports config is fetched
  useEffect(() => {
    if (!hasAttemptedFetch && !isLoadingConfig) {
      dispatch(fetchReportsConfig({}));
    }
  }, [dispatch, hasAttemptedFetch, isLoadingConfig]);

  // Breadcrumbs for error pages
  const errorBreadcrumbs = [{ text: 'Home', href: '/' }, { text: 'Reports' }];

  // Still loading config — show spinner while we determine access
  if (!hasAttemptedFetch || isLoadingConfig) {
    return <LoadingSpinner />;
  }

  // Invalid code or report not found — show "report does not exist" page
  if (!isValidCode || !currentReport || !currentReport.item_metadata.is_active) {
    return (
      <ReportsLayout breadcrumbItems={errorBreadcrumbs}>
        <NoAccessToVerity
          heading="This report does not exist"
          description="The report you are looking for could not be found. It may have been removed, deactivated, or the URL may be incorrect."
          imageSrc={null}
          buttonLabel="Go to Home"
          onButtonClick={() => navigate('/')}
          buttonExternal={false}
        />
      </ReportsLayout>
    );
  }

  // Check user access based on report visibility level — show "no access" page
  if (!canUserAccessReport(currentReport.report_visibility_level, userPermission)) {
    return (
      <ReportsLayout breadcrumbItems={errorBreadcrumbs}>
        <NoAccessToVerity
          heading="You do not have access to this report"
          description="This report is restricted and your current permissions do not allow access. Contact Verity support if you need access."
        />
      </ReportsLayout>
    );
  }

  // User has access — render the child route (ReportViewPage)
  return <Outlet />;
};

export default ReportRouteGuard;
